import React from 'react';
import Icon from '../../../components/AppIcon';
import {
  formatFrenchDate,
  formatFrenchDateTime,
  formatScheduledTimeDisplay,
  getQuoteProblemName,
  getQuoteServiceName,
} from '../appointmentTiming';

const formatPrice = (price) => {
  if (price == null || price === '') return null;
  const amount = Number(price);
  if (!Number.isFinite(amount)) return null;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

const STATUS_LABELS = {
  planned: 'Planifié',
  planifié: 'Planifié',
  planifie: 'Planifié',
  scheduled: 'Planifié',
  en_route: 'En route',
  'en route': 'En route',
  on_site: 'Sur place',
  'sur place': 'Sur place',
  in_progress: 'En cours',
  completed: 'Terminée',
  terminé: 'Terminée',
  termine: 'Terminée',
  cancelled: 'Annulée',
  canceled: 'Annulée',
  annulé: 'Annulée',
};

const DetailRow = ({ icon, label, children }) => {
  if (children == null || children === '') return null;
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon name={icon} size={20} className="text-primary" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{children}</p>
      </div>
    </div>
  );
};

const InterventionDetailsPanel = ({ appointment, status = '' }) => {
  const requestDateTime = formatFrenchDateTime(
    appointment?.createdAt || appointment?.created_at
  );
  const interventionDate = formatFrenchDate(
    appointment?.scheduledDate || appointment?.scheduled_date
  );
  const interventionTimeStart = formatScheduledTimeDisplay(
    appointment?.scheduledTimeStart || appointment?.scheduled_time_start
  );
  const interventionTimeEnd = formatScheduledTimeDisplay(
    appointment?.scheduledTimeEnd || appointment?.scheduled_time_end
  );
  const interventionTime =
    interventionTimeStart && interventionTimeEnd
      ? `${interventionTimeStart} - ${interventionTimeEnd}`
      : interventionTimeStart || '';

  const problemName = getQuoteProblemName(appointment);
  const serviceName = getQuoteServiceName(appointment);
  const hasNotes = Boolean(appointment?.notes?.trim());
  const priceLabel = formatPrice(
    appointment?.priceTotal ?? appointment?.price_total ?? appointment?.priceSubtotal
  );

  const statusKey = `${status || appointment?.status || ''}`.toLowerCase().trim();
  const statusLabel = STATUS_LABELS[statusKey] || status || 'Planifié';

  return (
    <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
      <div className="mb-6">
        <h2 className="text-sm font-medium text-foreground">
          {interventionDate
            ? `Demande d’intervention du ${interventionDate}`
            : 'Demande d’intervention'}
        </h2>
      </div>

      <div className="space-y-4">
        <DetailRow icon="Hash" label="Référence">
          {appointment?.reference || null}
        </DetailRow>

        <DetailRow icon="Activity" label="Statut">{statusLabel}</DetailRow>

        <DetailRow icon="AlertCircle" label="Problème">
          {problemName}
        </DetailRow>

        <DetailRow icon="Wrench" label="Solution proposée">
          {serviceName}
        </DetailRow>

        <DetailRow icon="FileText" label="Montant TTC">
          {priceLabel}
        </DetailRow>

        <DetailRow icon="MessageSquare" label="Précisions">
          {hasNotes ? appointment.notes : null}
        </DetailRow>

        <DetailRow icon="Calendar" label="Date de la demande">
          {requestDateTime || null}
        </DetailRow>

        <DetailRow icon="Clock" label="Date d’intervention">
          {interventionDate
            ? `${interventionDate}${interventionTime ? ` · ${interventionTime}` : ''}`
            : null}
        </DetailRow>

        <DetailRow icon="MapPin" label="Adresse">
          {appointment?.customerAddress || null}
        </DetailRow>
      </div>
    </div>
  );
};

export default InterventionDetailsPanel;
